import type { Key } from 'react';
import { memo, useMemo, useRef } from 'react';

import { Button as RACButton } from 'react-aria-components/Button';
import { ListBox, ListBoxItem } from 'react-aria-components/ListBox';
import {
  RadioButton,
  RadioField,
  RadioGroup,
} from 'react-aria-components/RadioGroup';
import { Select, SelectValue } from 'react-aria-components/Select';

import { Icon } from '../icon/icon';
import { Label } from '../label/label';
import { Popover } from '../popover/popover';
import { Text } from '../text/text';

import styles from './time_picker.module.css';

export type TimeFormatType = 'am' | 'pm' | '24h';

type Option = { value: string; label: string };

const buildOptions = (count: number, pad: number, start = 0): Option[] =>
  Array.from({ length: count }, (_, index) => {
    const value = String(index + start).padStart(pad, '0');
    return { value, label: value };
  });

const MINUTES_OPTIONS = buildOptions(60, 2, 0);
const SECONDS_OPTIONS = buildOptions(60, 2, 0);
const HOURS_24H_OPTIONS = buildOptions(24, 2, 0);
const HOURS_12H_OPTIONS = buildOptions(12, 2, 1);

type TimeSelectProps = {
  ariaLabel: string;
  items: Option[];
  selectedKey: string;
  onSelectionChange?: (key: Key | null) => void;
};

function TimeSelect({
  ariaLabel,
  items,
  selectedKey,
  onSelectionChange,
}: TimeSelectProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <Select
      aria-label={ariaLabel}
      selectedKey={selectedKey}
      onSelectionChange={onSelectionChange}
      className={styles.selector}
    >
      <RACButton ref={buttonRef} className={styles.selector_button}>
        <SelectValue className={styles.selector_value} />
        <Icon
          name="chevron_small_isolated_s"
          size="s"
          className={styles.selector_icon}
        />
      </RACButton>
      <Popover
        offset={2}
        maxHeight={210}
        className={styles.selector_popover}
        style={
          buttonRef.current
            ? {
                '--trigger-width': `${buttonRef.current.offsetWidth}px`,
              }
            : undefined
        }
      >
        <ListBox className={styles.selector_listbox} items={items}>
          {(item) => (
            <ListBoxItem
              id={item.value}
              textValue={item.label}
              className={styles.selector_item}
            >
              <Icon
                name="checkmark"
                className={styles.selector_checkmark}
                size="xs"
              />
              <div className={styles.selector_item_label_container}>
                <Text elementType="span" color="high_emphasis" variant="value">
                  {item.label}
                </Text>
              </div>
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </Select>
  );
}

export type TimePickerProps = {
  format: TimeFormatType;
  onFormatChange: (value: TimeFormatType) => void;

  hours: string;
  minutes: string;

  onHoursChange: (key: Key | null) => void;
  onMinutesChange: (key: Key | null) => void;

  showSeconds?: boolean;
  seconds?: string;
  onSecondsChange?: (key: Key | null) => void;
};

export const TimePicker = memo(function TimePickerComponent({
  format,
  onFormatChange,
  hours,
  minutes,
  onHoursChange,
  onMinutesChange,
  showSeconds = false,
  seconds,
  onSecondsChange,
}: TimePickerProps) {
  const hoursOptions = useMemo(
    () => (format === '24h' ? HOURS_24H_OPTIONS : HOURS_12H_OPTIONS),
    [format],
  );

  const normalizedHours = useMemo(() => {
    const hasMatchingHour = hoursOptions.some(
      (option) => option.value === hours,
    );
    if (hasMatchingHour) {
      return hours;
    }
    return '12';
  }, [hours, hoursOptions]);

  const normalizedMinutes = useMemo(() => {
    const hasMatchingMinute = MINUTES_OPTIONS.some(
      (option) => option.value === minutes,
    );
    if (hasMatchingMinute) {
      return minutes;
    }
    return '00';
  }, [minutes]);

  const normalizedSeconds = useMemo(() => {
    if (!showSeconds) {
      return '00';
    }
    const hasMatchingSecond = SECONDS_OPTIONS.some(
      (option) => option.value === seconds,
    );
    if (hasMatchingSecond) {
      return seconds ?? '00';
    }
    return '00';
  }, [seconds, showSeconds]);

  return (
    <div className={styles.content}>
      <RadioGroup
        className={styles.format}
        aria-label="Time format"
        value={format}
        onChange={(selectedFormat) => {
          if (
            selectedFormat === 'am' ||
            selectedFormat === 'pm' ||
            selectedFormat === '24h'
          ) {
            onFormatChange(selectedFormat);
          }
        }}
      >
        <RadioField value="am">
          <RadioButton className={styles.format_radio}>am</RadioButton>
        </RadioField>
        <RadioField value="pm">
          <RadioButton className={styles.format_radio}>pm</RadioButton>
        </RadioField>
        <RadioField value="24h">
          <RadioButton className={styles.format_radio}>24h</RadioButton>
        </RadioField>
      </RadioGroup>

      <div
        className={styles.selector_container}
        data-seconds={showSeconds ? 'true' : 'false'}
      >
        <div className={styles.selector_block}>
          <Label>Hours</Label>
          <TimeSelect
            ariaLabel="Hours"
            items={hoursOptions}
            selectedKey={normalizedHours}
            onSelectionChange={onHoursChange}
          />
        </div>

        <div className={styles.separator}>:</div>

        <div className={styles.selector_block}>
          <Label>Minutes</Label>
          <TimeSelect
            ariaLabel="Minutes"
            items={MINUTES_OPTIONS}
            selectedKey={normalizedMinutes}
            onSelectionChange={onMinutesChange}
          />
        </div>

        {showSeconds && (
          <>
            <div className={styles.separator}>:</div>
            <div className={styles.selector_block}>
              <Label>Seconds</Label>
              <TimeSelect
                ariaLabel="Seconds"
                items={SECONDS_OPTIONS}
                selectedKey={normalizedSeconds}
                onSelectionChange={onSecondsChange}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
});
